import { MaterialCommunityIcons } from '@expo/vector-icons';
import { DATE_FORMAT_MMDDYYYY, DayIntervalUnit } from '@gripp/shared-logic';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Colors } from '../../../themes/colors';
import {
  ControlledSelect,
  DatePickerSelector,
  useFormContext,
} from '../../forms';
import { BaseNumberInput } from './baseNumberInput';
import {
  AlertInputs,
  AlertType,
  ClearButtonProps,
  DeleteLogicProps,
} from './types';

type AlertLogicInputOldProps = {
  type: AlertType;
  isPrimaryAlertInput: boolean;
  uomLabel?: string;
  setAdditionalLogic: (value: boolean) => void;
};

export default function AlertLogicInputOld({
  type,
  isPrimaryAlertInput,
  uomLabel,
  setAdditionalLogic,
}: AlertLogicInputOldProps) {
  const {
    setValue,
    getValues,
    control,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();

  const dayIntervalUnitItems = [
    {
      label: t('alerts.dateInterval.minutes'),
      value: DayIntervalUnit.Minutes,
    },
    {
      label: t('alerts.dateInterval.hours'),
      value: DayIntervalUnit.Hours,
    },
    {
      label: t('alerts.dateInterval.days'),
      value: DayIntervalUnit.Days,
    },
    {
      label: t('alerts.dateInterval.weeks'),
      value: DayIntervalUnit.Weeks,
    },
    {
      label: t('alerts.dateInterval.months'),
      value: DayIntervalUnit.Months,
    },
  ];

  const FormErrorText = ({ fieldName }: { fieldName: string }) => {
    const error = errors[fieldName];
    if (!error?.message) {
      return null;
    }

    return <Text style={styles.errorText}>{error.message.toString()}</Text>;
  };

  const alertPlaceholder: Record<AlertType, { placeholder: string }> = {
    [AlertType.DaysSkipped]: { placeholder: t('alerts.numberOfDays') },
    [AlertType.Date]: { placeholder: DATE_FORMAT_MMDDYYYY },
    [AlertType.Interval]: { placeholder: t('alerts.setInterval') },
    [AlertType.Min]: { placeholder: t('alerts.number') },
    [AlertType.Max]: { placeholder: t('alerts.number') },
    [AlertType.RoutineCompletions]: { placeholder: t('alerts.numberOfTimes') },
    [AlertType.RecurringInterval]: { placeholder: t('alerts.setInterval') },
  };

  const ClearButton = ({ inputType }: ClearButtonProps) => {
    return (
      <Pressable
        hitSlop={8}
        onPress={() => {
          setValue(inputType, undefined, { shouldDirty: true });
        }}
      >
        <MaterialCommunityIcons name="close" size={16} />
      </Pressable>
    );
  };

  const DeleteInputButton = ({ inputType }: DeleteLogicProps) => {
    const handleDeleteAction = () => {
      if (isPrimaryAlertInput) {
        return;
      }

      setValue('secondInputField', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(inputType, undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue('secondAlertType', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAdditionalLogic(false);
    };

    return (
      <TouchableOpacity
        style={styles.deleteButton}
        activeOpacity={0.75}
        onPress={() => handleDeleteAction()}
      >
        <MaterialCommunityIcons
          name="delete-outline"
          size={24}
          color={Colors.grayText}
        />
      </TouchableOpacity>
    );
  };

  const renderInputComponent = (type: AlertType) => {
    switch (type) {
      case AlertType.Date:
        return (
          <View style={[styles.inputRow, { marginBottom: 8 }]}>
            <View style={[styles.inputContainer]}>
              <View style={styles.dateInput}>
                <DatePickerSelector
                  name="onDate"
                  setValue={setValue}
                  control={control}
                  label={'Date:'}
                  placeholder={alertPlaceholder[type].placeholder}
                  containerStyle={{
                    borderWidth: 0,
                    padding: 0,
                    paddingVertical: 4,
                    paddingBottom: 8,
                    justifyContent: 'center',
                    height: '100%',
                  }}
                  maxYears={5}
                  minDate={moment().add(1, 'day').format('YYYY-MM-DD')}
                />
              </View>
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.onDate} />
            )}
          </View>
        );

      case AlertType.RecurringInterval:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={[styles.inputRow, { marginBottom: 8 }]}>
              <View
                style={[
                  styles.inputContainer,
                  styles.inputNumberContainer,
                  errors.dayInterval && styles.inputError,
                ]}
              >
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.dayInterval}
                  placeholder={t('alerts.number')}
                  clearButton={ClearButton}
                  inputLabelValue={t('')}
                />
              </View>
              {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={AlertInputs.dayInterval} />
              )}
            </View>
            <FormErrorText fieldName="dayInterval" />
            <View style={[styles.inputRow, { marginBottom: 8, zIndex: 9999 }]}>
              <View
                style={[
                  styles.inputContainer,
                  { flex: 1, paddingHorizontal: 0 },
                  errors.dayIntervalUnit && styles.inputError,
                ]}
              >
                <ControlledSelect
                  name={AlertInputs.dayIntervalUnit}
                  control={control}
                  items={dayIntervalUnitItems}
                  listMode="SCROLLVIEW"
                  dropDownPickerProps={{
                    style: {
                      borderWidth: 0,
                      backgroundColor: 'transparent',
                      height: '100%',
                      minHeight: 'auto',
                    },
                    textStyle: styles.inputContent,
                    placeholder: t('alerts.onDayIntervalUnit'),
                    placeholderStyle: styles.placeholderLabel,
                    dropDownContainerStyle: {
                      borderWidth: 1,
                      borderColor: Colors.grayText,
                      backgroundColor: Colors.white,
                      marginTop: 4,
                      borderRadius: 8,
                      position: 'absolute',
                      top: '100%',
                    },
                  }}
                  label={''}
                />
              </View>
            </View>
            <FormErrorText fieldName="dayIntervalUnit" />
            <View style={[styles.inputRow]}>
              <View style={[styles.inputContainer]}>
                <View style={styles.dateInput}>
                  <DatePickerSelector
                    name={AlertInputs.daysStartDate}
                    setValue={setValue}
                    control={control}
                    label={t('alerts.startDate')}
                    placeholder={alertPlaceholder[AlertType.Date].placeholder}
                    containerStyle={{
                      borderWidth: 0,
                      padding: 0,
                      paddingVertical: 4,
                      paddingBottom: 8,
                      justifyContent: 'center',
                      height: '100%',
                    }}
                    maxYears={5}
                  />
                </View>
              </View>
            </View>
            <FormErrorText fieldName="daysStartDate" />
          </View>
        );

      case AlertType.Interval:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={[styles.inputRow, { marginBottom: 8 }]}>
              <View
                style={[styles.inputContainer, styles.inputNumberContainer]}
              >
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.onInterval}
                  placeholder={alertPlaceholder[type].placeholder}
                  clearButton={ClearButton}
                  inputLabelValue={t('alerts.intervalValue')}
                />
              </View>
              {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={AlertInputs.onInterval} />
              )}
            </View>
            <View style={[styles.inputRow]}>
              <View
                style={[styles.inputContainer, styles.inputNumberContainer]}
              >
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.onIntervalStart}
                  placeholder={t('alerts.startingValue')}
                  clearButton={ClearButton}
                  inputLabelValue={`${t('alerts.starting')} ${uomLabel}`}
                />
              </View>
            </View>
          </View>
        );

      case AlertType.Min:
        return (
          <View style={styles.inputRow}>
            <View style={[styles.inputContainer, styles.inputNumberContainer]}>
              <BaseNumberInput
                getValues={getValues}
                setValue={setValue}
                alertInputName={AlertInputs.minValue}
                placeholder={alertPlaceholder[type].placeholder}
                clearButton={ClearButton}
              />
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.minValue} />
            )}
          </View>
        );

      case AlertType.Max:
        return (
          <View style={styles.inputRow}>
            <View style={[styles.inputContainer, styles.inputNumberContainer]}>
              <BaseNumberInput
                getValues={getValues}
                setValue={setValue}
                alertInputName={AlertInputs.maxValue}
                placeholder={alertPlaceholder[type].placeholder}
                clearButton={ClearButton}
              />
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.maxValue} />
            )}
          </View>
        );

      case AlertType.RoutineCompletions:
        return (
          <View style={styles.inputRow}>
            <View style={[styles.inputContainer, styles.inputNumberContainer]}>
              <BaseNumberInput
                getValues={getValues}
                setValue={setValue}
                alertInputName={AlertInputs.completions}
                placeholder={alertPlaceholder[type].placeholder}
                clearButton={ClearButton}
              />
            </View>
            {!isPrimaryAlertInput && (
              <DeleteInputButton inputType={AlertInputs.completions} />
            )}
          </View>
        );

      case AlertType.DaysSkipped:
        return (
          <View style={styles.multipleInputContainer}>
            <View style={styles.inputRow}>
              <View style={[styles.inputContainer, styles.inputNumberContainer]}>
                <BaseNumberInput
                  getValues={getValues}
                  setValue={setValue}
                  alertInputName={AlertInputs.daysSkipped}
                  placeholder={alertPlaceholder[type].placeholder}
                  clearButton={ClearButton}
                />
              </View>
              {!isPrimaryAlertInput && (
                <DeleteInputButton inputType={AlertInputs.daysSkipped} />
              )}
            </View>
            <View style={[styles.inputRow, { marginTop: 8 }]}>
              <View style={[styles.inputContainer]}>
                <View style={styles.dateInput}>
                  <DatePickerSelector
                    name={AlertInputs.daysStartDate}
                    setValue={setValue}
                    control={control}
                    label={t('alerts.startDate')}
                    placeholder={alertPlaceholder[AlertType.Date].placeholder}
                    containerStyle={{
                      borderWidth: 0,
                      padding: 0,
                      paddingVertical: 4,
                      paddingBottom: 8,
                      justifyContent: 'center',
                      height: '100%',
                    }}
                    maxYears={5}
                  />
                </View>
              </View>
            </View>
          </View>
        );

      default:
        return (
          <View>
            <Text style={styles.placeholderLabel}>Select a logic...</Text>
          </View>
        );
    }
  };

  return <View style={styles.container}>{renderInputComponent(type)}</View>;
}

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  inputRow: {
    flexDirection: 'row',
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: Colors.grayText,
    height: 52,
    flex: 1,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
  },
  inputNumberContainer: {
    paddingHorizontal: 0,
  },
  multipleInputContainer: {
    flex: 1,
  },
  dateInput: {
    flex: 1,
    height: '100%',
  },
  inputButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    borderWidth: 0.5,
    borderColor: 'lightblue',
    marginRight: 5,
    fontSize: 16,
    fontWeight: '400',
  },
  placeholderLabel: {
    color: Colors.grayFill,
    fontSize: 16,
    fontWeight: '400',
    flex: 1,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12,
  },
  inputError: {
    borderColor: Colors.red,
  },
  inputContent: {
    fontSize: 16,
    fontWeight: '400',
    color: Colors.black,
  },
  errorText: {
    color: Colors.red,
    fontSize: 12,
    marginTop: 4,
    marginBottom: 4,
  },
});
